import type { PurchasesOfferings, PurchasesOffering, PurchasesPackage } from '@revenuecat/purchases-capacitor';

/**
 * Package identifier for the one-time discount offering
 * This is a custom package type used for promotional discounts
 */
export const DISCOUNT_PACKAGE_IDENTIFIER = 'one_time_discount' as const;

/**
 * Extended offering interface that includes the one_time_discount property
 * RevenueCat's types don't include this custom property, so we extend it
 */
export interface ExtendedPurchasesOffering extends PurchasesOffering {
  one_time_discount?: PurchasesPackage;
}

/**
 * Fallback pricing constants when package prices are unavailable
 */
export const FALLBACK_PRICES = {
  ANNUAL: 104,
  MONTHLY: 10,
  WEEKLY: 5,
  DISCOUNT_ANNUAL: 78,
} as const;

/**
 * Preferred offering IDs to check first when searching for packages
 */
export const PREFERRED_OFFERING_IDS = ['weekly_yearly_pricing', 'new_pricing', 'default'] as const;

/**
 * Find a package by type in an offering
 * @param offering - The offering to search in
 * @param type - The package type to find (e.g., 'ANNUAL', 'MONTHLY')
 * @returns The found package or null
 */
export const findPackageByType = (
  offering: PurchasesOffering | null | undefined,
  type: string,
): PurchasesPackage | null => {
  if (!offering) return null;

  // Try to find in availablePackages first
  if (offering.availablePackages && Array.isArray(offering.availablePackages)) {
    const pkg = offering.availablePackages.find((p: PurchasesPackage) => p.packageType === type);
    if (pkg) return pkg;
  }

  // Fallback to direct properties
  if (type === 'ANNUAL' && offering.annual) return offering.annual;
  if (type === 'MONTHLY' && offering.monthly) return offering.monthly;
  if (type === 'WEEKLY' && offering.weekly) return offering.weekly;

  return null;
};

/**
 * Find a package by type across all offerings
 * @param offerings - The offerings object containing all offerings
 * @param type - The package type to find (e.g., 'ANNUAL', 'WEEKLY')
 * @param preferredOfferingIds - Optional array of offering identifiers to check first
 * @returns The found package or null
 */
export const findPackageInAllOfferings = (
  offerings: PurchasesOfferings | null,
  type: string,
  preferredOfferingIds?: readonly string[],
): PurchasesPackage | null => {
  if (!offerings) return null;

  // First try preferred offerings if provided
  if (preferredOfferingIds && offerings.all) {
    for (const offeringId of preferredOfferingIds) {
      const offering = offerings.all[offeringId];
      if (offering) {
        const pkg = findPackageByType(offering, type);
        if (pkg) return pkg;
      }
    }
  }

  // Try current offering
  if (offerings.current) {
    const pkg = findPackageByType(offerings.current, type);
    if (pkg) return pkg;
  }

  // Search through all offerings
  if (offerings.all) {
    for (const offeringId in offerings.all) {
      const offering = offerings.all[offeringId];
      if (offering) {
        const pkg = findPackageByType(offering, type);
        if (pkg) return pkg;
      }
    }
  }

  return null;
};

/**
 * Validate if an offering has valid packages
 * @param offering - The offering to validate
 * @returns True if the offering has at least one valid package
 */
export const hasValidPackages = (offering: PurchasesOffering | null | undefined): boolean => {
  if (!offering) return false;

  // Check availablePackages array
  if (offering.availablePackages && Array.isArray(offering.availablePackages)) {
    return offering.availablePackages.length > 0;
  }

  // Check direct properties
  return !!(offering.annual || offering.monthly || offering.weekly || offering.lifetime);
};

/**
 * Validate if offerings result contains at least one offering with valid packages
 * @param offerings - The offerings result from RevenueCat
 * @returns True if at least one offering has valid packages
 */
export const hasValidOfferingsInResult = (offerings: PurchasesOfferings | null): boolean => {
  if (!offerings) return false;

  // Check current offering first
  if (offerings.current && hasValidPackages(offerings.current)) {
    return true;
  }

  // Check all offerings
  if (offerings.all) {
    return Object.values(offerings.all).some((offering) => hasValidPackages(offering));
  }

  return false;
};

/**
 * Derive the number of free-trial days from a RevenueCat package's introductory price.
 *
 * RevenueCat exposes trial length via `product.introPrice` on the package. This helper
 * centralises the calculation so `OneTimeOfferPage` and `PaymentModalByPreference` share
 * one implementation instead of diverging over time.
 *
 * @param pkg - The RevenueCat package to inspect (may be null/undefined)
 * @param fallback - Days to return when no trial info is available (default: 7)
 * @returns Number of free-trial days
 */
export const getOfferTrialDays = (pkg: PurchasesPackage | null | undefined, fallback = 7): number => {
  const intro = pkg?.product?.introPrice;
  if (intro && intro.price === 0 && intro.periodUnit && intro.periodNumberOfUnits) {
    const unit = intro.periodUnit.toUpperCase();
    const n = intro.periodNumberOfUnits;
    if (unit === 'DAY') return n;
    if (unit === 'WEEK') return n * 7;
    if (unit === 'MONTH') return n * 30;
    if (unit === 'YEAR') return n * 365;
  }
  return fallback;
};

/**
 * Find a discount package in an offering
 * @param offering - The offering to search in
 * @returns The found discount package or null
 */
export const findDiscountPackage = (offering: PurchasesOffering | null | undefined): PurchasesPackage | null => {
  if (!offering) return null;

  // Try to find in availablePackages first
  if (offering.availablePackages && Array.isArray(offering.availablePackages)) {
    const pkg = offering.availablePackages.find((p: PurchasesPackage) => p.identifier === DISCOUNT_PACKAGE_IDENTIFIER);
    if (pkg) return pkg;
  }

  // Fallback to direct property (using extended type for custom RevenueCat property)
  return (offering as ExtendedPurchasesOffering).one_time_discount || null;
};
